<template>
  <ProgressBar
    :primary-color="primaryColor"
    progress="1"
  />

  <div>
    <p class="font-bold text-xl pb-4 text-center w-full">
      {{ __('label_gender') }}
    </p>

    <div class="flex justify-center">
      <div
        class="w-20 md:w-24 h-20 md:h-24 cursor-pointer"
        @click="selectGender('male')"
      >
        <MaleIcon
          :primary-color="primaryColor"
          :selected="$store.state.form.gender === 'male'"
        />
      </div>

      <div
        class="w-20 md:w-24 h-20 md:h-24 ml-6 cursor-pointer"
        @click="selectGender('female')"
      >
        <FemaleIcon
          :primary-color="primaryColor"
          :selected="$store.state.form.gender === 'female'"
        />
      </div>
    </div>

    <ErrorMessage name="gender" />
  </div>

  <div>
    <p class="font-bold text-xl pb-4 text-center">
      {{ __('label_birthdate') }}
    </p>

    <div class="flex justify-center">
      <div class="grid grid-cols-3 gap-x-4 w-full">
        <input
          v-model="birthDay"
          name="birth_day"
          class="w-full h-8 rounded-md border-2 placeholder-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          type="number"
          min="1"
          max="31"
          :placeholder="__('label_birthday')"
          :style="{
            borderColor: `#${primaryColor}`,
          }"
          @input="handleBirthDayChange()"
          @keydown.enter="submit()"
        >

        <input
          ref="birthMonthInput"
          v-model="birthMonth"
          name="birth_month"
          class="w-full h-8 rounded-md border-2 placeholder-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          type="number"
          min="1"
          max="12"
          :placeholder="__('label_birthmonth')"
          :style="{
            borderColor: `#${primaryColor}`,
          }"
          @input="handleBirthMonthChange()"
          @keydown.enter="submit()"
        >

        <input
          ref="birthYearInput"
          v-model="birthYear"
          name="birth_year"
          class="w-full h-8 rounded-md border-2 placeholder-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          type="number"
          min="1900"
          :max="new Date().getFullYear()"
          :placeholder="__('label_birthyear')"
          :style="{
            borderColor: `#${primaryColor}`,
          }"
          @input="handleBirthYearChange()"
          @keydown.enter="submit()"
        >
      </div>
    </div>

    <ErrorMessage name="birth_date" />
  </div>

  <FormFooter
    :button-color="buttonColor"
    :can-submit="canSubmit"
    :step="1"
    @submit="submit()"
  />
</template>

<script setup>
import FemaleIcon from '@timon/js/Components/FemaleIcon.vue';
import MaleIcon from '@timon/js/Components/MaleIcon.vue';
import {computed, ref} from 'vue';
import FormFooter from '@timon/js/Templates/CO-REG-LANDER-002/Components/FormFooter.vue';
import ErrorMessage from '@timon/js/Components/ErrorMessage.vue';
import {useStore} from 'vuex';
import ProgressBar from '@timon/js/Templates/CO-REG-LANDER-002/Components/ProgressBar.vue';
import {__, isPreview} from '@timon/js/Mixins/helpers';

const emit = defineEmits(['submit']);

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },

  canSubmit: {
    type: Boolean,
    required: true,
  },
});

const birthDay = ref(null);
const birthMonth = ref(null);
const birthYear = ref(null);

const previousBirthDay = ref(null);
const previousBirthMonth = ref(null);

const birthMonthInput = ref(null);
const birthYearInput = ref(null);

const birthDate = computed(() => {
  if (!birthDay.value || !birthMonth.value || !birthYear.value) {
    return null;
  }

  // convert to Y-m-d
  const day = birthDay.value.toString().padStart(2, '0');
  const month = birthMonth.value.toString().padStart(2, '0');

  return `${birthYear.value}-${month}-${day}`;
});

const primaryColor = ref(props.config.style?.primary);
const buttonColor = ref(props.config.style?.buttons);
const store = useStore();

function handleBirthDayChange() {
  if (birthDay.value > 31) {
    birthDay.value = 31;
  }

  if (birthDay.value > 3 || previousBirthDay.value === 0) {
    birthMonthInput.value.focus();
  }

  previousBirthDay.value = birthDay.value;
  handleBirthDateChange();
}

function handleBirthMonthChange() {
  if (birthMonth.value > 12) {
    birthMonth.value = 12;
  }

  if (birthMonth.value > 1 || previousBirthMonth.value === 0) {
    birthYearInput.value.focus();
  }

  previousBirthMonth.value = birthMonth.value;
  handleBirthDateChange();
}

function handleBirthYearChange() {
  if (birthYear.value.toString().length > 4) {
    birthYear.value = birthYear.value.toString().substring(0, 4);
  }

  handleBirthDateChange();
}

function handleBirthDateChange() {
  store.state.form.birth_date = birthDate.value;
}

function selectGender(gender) {
  store.state.form.gender = gender;
}

const allFieldsFilled = computed(() => {
  return !!store.state.form.gender && !!store.state.form.birth_date;
});

function submit() {
  store.commit('errors/clearErrors');

  if (isPreview()) {
    emit('submit');
    return;
  }

  if (!props.canSubmit) {
    return;
  }

  if (!allFieldsFilled.value) {
    setRequiredErrors();

    return;
  }

  setValidationErrors();

  if (Object.keys(store.state.errors).length > 0) {
    return;
  }

  emit('submit');
}

function setValidationErrors() {
  let errors = {};

  if (birthDay.value < 1 || birthDay.value > 31) {
    errors = {
      ...errors,
      'birth_date': [__('validation_invalid_birth_date')],
    };
  }

  if (birthMonth.value < 1 || birthMonth.value > 12) {
    errors = {
      ...errors,
      'birth_date': [__('validation_invalid_birth_date')],
    };
  }

  if ((birthYear.value < new Date().getFullYear() - 91) || (birthYear.value > new Date().getFullYear() - 18)) {
    errors = {
      ...errors,
      'birth_date': [__('validation_invalid_birth_date')],
    };
  }

  store.commit('errors/setErrors', errors);
}

function setRequiredErrors() {
  let errors = {};

  if (!store.state.form.gender) {
    errors = {
      ...errors,
      'gender': [__('validation_field_required').replace(':attribute', __('label_gender'))],
    };
  }

  if (!store.state.form.birth_date) {
    errors = {
      ...errors,
      'birth_date': [__('validation_field_required').replace(':attribute', __('label_birthdate'))],
    };
  }

  store.commit('errors/setErrors', errors);
}
</script>
